import type { GridItemChangeEvent } from "@tql/ui-components-react/kendo/grid";
import { useCallback, useMemo } from "react";

import {
	APPOINTMENT_SERVICE_NAME,
	RESIDENTIAL_SERVICE_NAME,
	usePickupDeliveryDetails,
} from "~/features/quote/components/quote-details/components/pickup-delivery-details/hooks/use-pickup-delivery-details.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { SubGroupKey } from "~/shared/types/quote-details-types.ts";
import { getAccessorialIds } from "~/shared/utils/address-book-accessorials.ts";

type UseAddressAccessorialSelectionProps = {
	dataItem: AddressBookItemSummary;
	name: SubGroupKey;
	itemChange: (event: GridItemChangeEvent) => void;
};

/**
 * Shared accessorial radio/checkbox selection logic for an address book item's
 * selected stop's contact detail, used by both the desktop row-detail expansion
 * and the mobile edit modal so the Residential/Appointment Delivery coupling
 * rules stay in one place.
 */
export function useAddressAccessorialSelection({
	dataItem,
	name,
	itemChange,
}: UseAddressAccessorialSelectionProps) {
	const { locationTypeOptions, services } = usePickupDeliveryDetails(name);
	const serviceOptions = useMemo(() => services[name] || [], [services, name]);
	const stopType = name === "pickup" ? 1 : 2;
	const detail = dataItem.details?.find(
		(addressDetail) => addressDetail.stopType === stopType,
	);

	const isOptionSelected = useCallback(
		(option: Accessorial): boolean => {
			const ids = getAccessorialIds(detail?.accessorials);
			return ids.includes(option.id);
		},
		[detail?.accessorials],
	);

	const handleRadioSelectionChange = useCallback(
		(option: Accessorial): void => {
			// Get current accessorial IDs
			const currentIds = getAccessorialIds(detail?.accessorials);
			// Keep only service options (filter out location types)
			const serviceIds = currentIds.filter((id) =>
				serviceOptions.some((service) => service.id === id),
			);
			// Add the new location type option
			let updatedIds = [...serviceIds, option.id];

			if (name === "delivery") {
				if (option.name === RESIDENTIAL_SERVICE_NAME) {
					const appointmentOption = serviceOptions.find(
						(s) => s.name === APPOINTMENT_SERVICE_NAME,
					);
					if (appointmentOption && !updatedIds.includes(appointmentOption.id)) {
						updatedIds = [...updatedIds, appointmentOption.id];
					}
				} else {
					const appointmentOption = serviceOptions.find(
						(s) => s.name === APPOINTMENT_SERVICE_NAME,
					);
					if (appointmentOption) {
						updatedIds = updatedIds.filter((id) => id !== appointmentOption.id);
					}
				}
			}

			itemChange({
				dataItem,
				field: "accessorials",
				value: updatedIds,
			} as GridItemChangeEvent);
		},
		[dataItem, detail?.accessorials, itemChange, serviceOptions, name],
	);

	const handleCheckboxSelectionAdd = useCallback(
		(option: Accessorial) => {
			const ids = getAccessorialIds(detail?.accessorials);
			if (!ids.includes(option.id)) {
				const updatedIds = [...ids, option.id];
				itemChange({
					dataItem,
					field: "accessorials",
					value: updatedIds,
				} as GridItemChangeEvent);
			}
		},
		[dataItem, itemChange, detail?.accessorials],
	);

	const handleCheckboxSelectionRemove = useCallback(
		(option: Accessorial) => {
			const ids = getAccessorialIds(detail?.accessorials);
			const updatedIds = ids.filter((id) => id !== option.id);
			itemChange({
				dataItem,
				field: "accessorials",
				value: updatedIds,
			} as GridItemChangeEvent);
		},
		[dataItem, itemChange, detail?.accessorials],
	);

	const handleCombinedSelectionChange = useCallback(
		(option: Accessorial, checked: boolean) => {
			if (
				name === "delivery" &&
				option.name === APPOINTMENT_SERVICE_NAME &&
				!checked
			) {
				const currentIds = getAccessorialIds(detail?.accessorials);
				const isResidentialSelected = currentIds.some((id) =>
					locationTypeOptions.some(
						(l) => l.id === id && l.name === RESIDENTIAL_SERVICE_NAME,
					),
				);
				if (isResidentialSelected) {
					return;
				}
			}

			if (checked) {
				handleCheckboxSelectionAdd(option);
			} else {
				handleCheckboxSelectionRemove(option);
			}
		},
		[
			handleCheckboxSelectionAdd,
			handleCheckboxSelectionRemove,
			name,
			detail?.accessorials,
			locationTypeOptions,
		],
	);

	return {
		locationTypeOptions,
		serviceOptions,
		isOptionSelected,
		handleRadioSelectionChange,
		handleCombinedSelectionChange,
	};
}
